import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listPlans,
  listPlanRates,
  getNpaNxxRateSummary,
  uploadNpaNxxRates,
  type Rate,
} from "../api/plans";

function fmtMoney(n: number): string {
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

// service_flag bitmask — SERVICE_VOICE=1, FAX=2, SMS=4, EMAIL=8
function serviceLabel(flag: number): string {
  const parts: string[] = [];
  if (flag & 1) parts.push("Voice");
  if (flag & 2) parts.push("Fax");
  if (flag & 4) parts.push("SMS");
  if (flag & 8) parts.push("Email");
  return parts.join("+") || "—";
}

function RatesPanel({ planId, planName }: { planId: number; planName: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["plan-rates", planId],
    queryFn: () => listPlanRates(planId),
  });

  if (isLoading) return <p className="text-gray-500 text-sm mt-3">Loading rates…</p>;
  const rates = data ?? [];

  return (
    <div className="mt-4">
      <h2 className="text-lg font-semibold mb-2">
        Rates for <span className="text-brand">{planName}</span> ({rates.length})
      </h2>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr>
              <th className="px-4 py-2">Destination</th>
              <th className="px-4 py-2">Service</th>
              <th className="px-4 py-2 text-right">Rate</th>
              <th className="px-4 py-2 text-right">Increment</th>
              <th className="px-4 py-2">Type</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rates.map((r: Rate) => (
              <tr key={r.rate_id}>
                <td className="px-4 py-2">#{r.destination_id}</td>
                <td className="px-4 py-2">{serviceLabel(r.service_flag)}</td>
                <td className="px-4 py-2 text-right font-mono">{fmtMoney(r.rate)}</td>
                <td className="px-4 py-2 text-right">{r.billing_increment}s</td>
                <td className="px-4 py-2 text-gray-500">{r.billing_type}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rates.length === 0 && <p className="text-gray-400 text-sm p-4">No rates on this plan.</p>}
      </div>
    </div>
  );
}

function NpaNxxRateSheet() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["npa-nxx-summary"],
    queryFn: getNpaNxxRateSummary,
  });
  const [msg, setMsg] = useState<string | null>(null);
  const upload = useMutation({
    mutationFn: (file: File) => uploadNpaNxxRates(file),
    onSuccess: (r) => {
      setMsg(`Loaded ${r.loaded.toLocaleString()} rows (${r.skipped.toLocaleString()} skipped).`);
      qc.invalidateQueries({ queryKey: ["npa-nxx-summary"] });
    },
    onError: (e) => setMsg(`Upload failed: ${(e as Error).message}`),
  });

  return (
    <div className="bg-white rounded-lg shadow p-4 mb-6">
      <h2 className="text-lg font-semibold mb-1">NPA-NXX rate sheet</h2>
      <p className="text-xs text-gray-500 mb-3">
        Used for accounts on the “NPA-NXX” billing mode. 60/6 billing; the interstate or
        intrastate rate is chosen per call from the caller-ID and destination states.
      </p>
      {isLoading ? (
        <p className="text-gray-500 text-sm">Loading…</p>
      ) : (
        <div className="text-sm text-gray-700 space-y-1">
          <div>
            <span className="text-gray-500">Rows loaded:</span>{" "}
            {(data?.rows ?? 0).toLocaleString()}
          </div>
          <div>
            <span className="text-gray-500">Catch-all (per min):</span> interstate{" "}
            {data?.default_interstate ?? "—"} / intrastate {data?.default_intrastate ?? "—"}
          </div>
          {data?.updated_at && (
            <div className="text-xs text-gray-400">
              Updated {new Date(data.updated_at).toLocaleString()}
            </div>
          )}
        </div>
      )}
      <div className="mt-3 flex items-center gap-3">
        <input
          type="file"
          accept=".csv"
          disabled={upload.isPending}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) upload.mutate(f);
            e.target.value = "";
          }}
          className="text-sm"
        />
        {upload.isPending && <span className="text-sm text-gray-500">Uploading…</span>}
      </div>
      {msg && <p className="text-sm mt-2">{msg}</p>}
      <p className="text-xs text-gray-400 mt-2">
        CSV columns: NPANXX, InterstateRate, IntrastateRate (a <code>*</code> row is the
        catch-all). Replaces the entire sheet. Admin only.
      </p>
    </div>
  );
}

export function PlansPage() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["plans"],
    queryFn: listPlans,
  });
  const [selected, setSelected] = useState<{ id: number; name: string } | null>(null);

  if (isLoading) return <p className="text-gray-500">Loading plans…</p>;
  if (isError) return <p className="text-red-600">{(error as Error).message}</p>;

  const plans = data ?? [];

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Billing Plans</h1>
      <NpaNxxRateSheet />
      {plans.length === 0 ? (
        <p className="text-gray-500">No plans.</p>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-2">Plan</th>
                <th className="px-4 py-2">Description</th>
                <th className="px-4 py-2">Billing cycle</th>
                <th className="px-4 py-2 text-right">DID monthly</th>
                <th className="px-4 py-2 text-right">Weekly fee</th>
                <th className="px-4 py-2">Default</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {plans.map((p) => (
                <tr
                  key={p.plan_id}
                  className={selected?.id === p.plan_id ? "bg-brand/5" : ""}
                >
                  <td className="px-4 py-2 font-medium">{p.name}</td>
                  <td className="px-4 py-2 text-gray-600">{p.description || "—"}</td>
                  <td className="px-4 py-2">{p.billing_cycle}</td>
                  <td className="px-4 py-2 text-right">{fmtMoney(p.did_monthly_rate)}</td>
                  <td className="px-4 py-2 text-right">{fmtMoney(p.weekly_fee)}</td>
                  <td className="px-4 py-2">{p.is_default ? "✓" : ""}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => setSelected({ id: p.plan_id, name: p.name })}
                      className="text-brand hover:underline"
                    >
                      View rates
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && <RatesPanel planId={selected.id} planName={selected.name} />}
    </div>
  );
}
